import { useLoaderData } from '@remix-run/react'
import { FlexContainer, FlexItem } from '../Grid'
import ChevronLeftIcon from '../icons/ChevronLeft'
import { ResponsiveImage } from '../ResponsiveImage'
import { flattedParseJSON } from '~/utils/flatted/json'
import { trackEvent } from '~/utils/tracking/trackEvent'
import { Button } from '~/components/general/Button'
import classNames from 'classnames'
import { TypeResumeExample } from '~/types/contentful'
import { getContentfulAsset } from '~/libs/contentful/helpers'

type HeroProps = {
  type: 'resume' | 'coverLetter'
}

export const Hero = ({ type }: HeroProps) => {
  let { resumeExample } = useLoaderData() as {
    resumeExample: TypeResumeExample<'WITHOUT_UNRESOLVABLE_LINKS', string>
  }
  const fallbackExamples = type === 'resume' ? '/resume-examples' : '/cover-letter-examples'
  resumeExample = flattedParseJSON(resumeExample)
  const { title, examplePreview, exampleLink } = resumeExample?.fields || resumeExample

  const exampleEntry = getContentfulAsset(examplePreview)
  return (
    <FlexContainer
      className="max-md:p-[6px_10px_46px] max-lg:p-[26px_26px_56px] max-xl:p-[34px_34px_40px] p-[36px_36px_52px] mb-15 max-xl:mb-5 max-md:h-[535px] bg-neutral-5 h-[550px] max-lg:h-[565px] xl:justify-between justify-center"
      style={{ borderRadius: '24px', maxWidth: '100%', overflow: 'hidden' }}
    >
      <FlexItem
        desktop={6}
        laptop={6}
        phone={7}
        tablet={9}
        className="lg:h-full lg:flex flex-col justify-between xl:w-fit lg:relative"
      >
        <Button
          iconLeft={<ChevronLeftIcon />}
          text={'All examples'}
          type={'link'}
          color={'transparent'}
          className={'border border-neutral-15'}
          href={fallbackExamples}
        />
        <h1 className="text-2xl lg:bottom-0 lg:min-h-37 pt-5 xl:max-w-[511px]">{title}</h1>
      </FlexItem>

      <FlexItem
        desktop={6}
        laptop={6}
        phone={7}
        tablet={9}
        style={{
          position: 'relative',
          alignContent: 'center',
        }}
      >
        {exampleEntry && (
          <div className="block my-0 mx-auto max-w-[544px] max-lg:w-[467px] max-md:w-[285px]">
            <ResponsiveImage asset={exampleEntry?.fields || exampleEntry} />
          </div>
        )}
        <Button
          text={type === 'resume' ? 'Edit this resume' : 'Edit this cover letter'}
          type={'link'}
          href={exampleLink}
          className={classNames(
            'absolute left-0 right-0 mx-auto bottom-[133px] md:bottom-[386px] xl:bottom-[340px]',
            type === 'resume'
              ? 'w-40 md:w-44 lg:w-45 xl:w-50'
              : 'w-[190px] md:w-[210px] lg:w-[214px] xl:w-60',
          )}
          onClick={() => {
            trackEvent('click_cta_hero', {
              website_host: 'career.io',
              label: type === 'resume' ? 'resume_examples' : 'cover_letter_examples',
              cta: type === 'resume' ? 'edit_resume' : 'edit_cover_letter',
              url: exampleLink,
              type: type,
            })
          }}
        />
      </FlexItem>
    </FlexContainer>
  )
}
